import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Briefcase, Loader2, AlertCircle, Mail, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkMode, setMagicLinkMode] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [sendingMagicLink, setSendingMagicLink] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      // Check if user needs to change password
      if (result.mustChangePassword) {
        navigate('/set-new-password');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Login error:', err);
      
      // Better error handling
      if (err.response) {
        // Server responded with an error
        setError(err.response.data?.detail || 'Invalid email or password');
      } else if (err.request) {
        // Request was made but no response (network issue / Cloudflare block)
        setError('Unable to connect to server. Please check your internet connection and try again.');
      } else {
        // Something else went wrong
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    setError('');
    setSendingMagicLink(true);

    try {
      await axios.post(`${API}/auth/magic-link/request`, { email });
      setMagicLinkSent(true);
    } catch (err) {
      // Even on error, show success to prevent email enumeration
      setMagicLinkSent(true);
    } finally {
      setSendingMagicLink(false);
    }
  };

  // Magic Link Sent Confirmation
  if (magicLinkSent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center justify-center mb-8">
            <img src="/logo.png" alt="Center Line Locating" className="h-24 w-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-wide">FieldReady</h1>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">Check Your Email</h2>
                <p className="text-slate-600 mb-4">
                  We've sent a login link to:<br />
                  <span className="font-medium text-slate-900">{email}</span>
                </p>
                <p className="text-sm text-slate-500 mb-6">
                  Click the link in the email to log in. The link expires in 30 minutes.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setMagicLinkSent(false);
                    setMagicLinkMode(false);
                  }}
                >
                  Back to Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Center Line Locating Logo */}
        <div className="flex flex-col items-center justify-center mb-8">
          <img 
            src="/logo.png" 
            alt="Center Line Locating" 
            className="h-24 w-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-wide">FieldReady</h1>
          <p className="text-sm text-slate-500">Job Scheduling & Management</p>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl font-['Barlow_Condensed']">
              {magicLinkMode ? 'Email Login Link' : 'Welcome Back'}
            </CardTitle>
            <CardDescription>
              {magicLinkMode 
                ? 'We\'ll email you a link to log in instantly' 
                : 'Sign in to access your account'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {magicLinkMode ? (
              // Magic Link Form
              <form onSubmit={handleMagicLinkRequest} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="bg-red-50 border-red-200">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="magic-email">Email</Label>
                  <Input
                    id="magic-email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-slate-50 border-slate-200 focus:border-amber-500 focus:ring-amber-500/20"
                    data-testid="magic-link-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-medium py-2.5"
                  disabled={sendingMagicLink}
                  data-testid="magic-link-submit-btn"
                >
                  {sendingMagicLink ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Email Me a Login Link
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-slate-600"
                  onClick={() => setMagicLinkMode(false)}
                >
                  Back to password login
                </Button>
              </form>
            ) : (
              // Password Login Form
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="bg-red-50 border-red-200">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-slate-50 border-slate-200 focus:border-amber-500 focus:ring-amber-500/20"
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-amber-600 hover:text-amber-700"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-slate-50 border-slate-200 focus:border-amber-500 focus:ring-amber-500/20"
                    data-testid="login-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-2.5"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>

                <p className="mt-3 text-center text-xs text-slate-400" data-testid="login-terms-consent-text">
                  By signing in, you agree to our{' '}
                  <Link to="/terms-of-service" className="text-slate-500 hover:text-slate-700 underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy-policy" className="text-slate-500 hover:text-slate-700 underline">Privacy Policy</Link>.
                </p>

                {/* Magic Link Option */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-500">or</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => setMagicLinkMode(true)}
                  data-testid="magic-link-option-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email Me a Login Link
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link to="/accept-invite" className="text-sm text-amber-600 hover:text-amber-700 font-medium">
                Have an invite code? Join your team
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-slate-400">
          FieldReady - Streamline your operations, assign tasks efficiently
        </p>
        <p className="mt-2 text-center">
          <Link to="/privacy-policy" className="text-xs text-slate-400 hover:text-slate-600">Privacy Policy</Link>
          <span className="text-xs text-slate-300 mx-2">|</span>
          <Link to="/terms-of-service" className="text-xs text-slate-400 hover:text-slate-600">Terms of Service</Link>
        </p>
      </div>
    </div>
  );
}
